import React, { useState } from "react";
import { Trash2, Upload, FileText, Eye } from "react-feather";
import toast from "react-hot-toast";
import AttachmentPreviewModal from "../../../../components/common/AttachmentPreviewModal";
import {
  useUploadMaintenanceRequestAttachmentMutation,
  useDeleteMaintenanceRequestAttachmentMutation,
  useLazyGetMaintenanceRequestAttachmentContentQuery,
} from "../vehiclesApi";

// The one attachment on a vehicle maintenance request (2026-09-23): the odometer
// photo the app takes at submission, OR anything else - a photo or a PDF - attached
// from here at any time. One slot, by the user's own choice; a new upload replaces
// what was there. Same upload / view (blob) / remove widget as the petty cash receipt.
const VehicleRequestAttachment = ({ recordGuid, hasAttachment, fileName, canManage }) => {
  const [uploadAttachment, { isLoading: isUploading }] = useUploadMaintenanceRequestAttachmentMutation();
  const [deleteAttachment] = useDeleteMaintenanceRequestAttachmentMutation();
  const [fetchContent] = useLazyGetMaintenanceRequestAttachmentContentQuery();
  const [selectedFile, setSelectedFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);

  const handleUpload = async () => {
    if (!selectedFile) return toast.error("Choose a file first");
    if (selectedFile.size > 2 * 1024 * 1024) return toast.error("File must be 2MB or smaller");
    try {
      await uploadAttachment({ recordGuid, file: selectedFile }).unwrap();
      toast.success("Attachment saved");
      setSelectedFile(null);
      setReplacing(false);
    } catch (error) {
      toast.error(error?.message || "Could not attach the file");
    }
  };

  const handleView = async () => {
    setIsOpening(true);
    try {
      const blob = await fetchContent(recordGuid).unwrap();
      setPreviewBlob(blob);
      setShowPreview(true);
    } catch (error) {
      toast.error(error?.message || "Could not open the attachment");
    } finally {
      setIsOpening(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await deleteAttachment(recordGuid).unwrap();
      toast.success("Attachment removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the attachment");
    }
  };

  const picker = (
    <div className="d-flex gap-2 flex-wrap">
      <input
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        className="form-control form-control-sm"
        style={{ maxWidth: 280 }}
        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
      />
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleUpload} disabled={isUploading || !selectedFile}>
        <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : hasAttachment ? "Replace" : "Upload"}
      </button>
      {replacing && (
        <button type="button" className="btn btn-sm btn-link" onClick={() => { setReplacing(false); setSelectedFile(null); }}>
          Cancel
        </button>
      )}
    </div>
  );

  return (
    <div className="rounded p-2 mb-2" style={{ background: "#F1F2F4", color: "#212529" }}>
      <div className="fw-semibold mb-1">Attachment <span className="fw-normal text-muted small">(photo or PDF, up to 2MB)</span></div>
      {hasAttachment ? (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <FileText size={16} className="text-muted" />
          <span>{fileName || "Attachment"}</span>
          <button type="button" className="btn btn-link p-1" onClick={handleView} disabled={isOpening} title="View">
            <Eye size={16} />
          </button>
          {canManage && !replacing && (
            <>
              <button type="button" className="btn btn-sm btn-link p-1" onClick={() => setReplacing(true)}>Replace</button>
              <button type="button" className="btn btn-link p-1 text-danger" onClick={handleDelete} title="Remove">
                <Trash2 size={16} />
              </button>
            </>
          )}
          {canManage && replacing && picker}
        </div>
      ) : canManage ? (
        picker
      ) : (
        <div className="text-muted small">Nothing attached.</div>
      )}

      <AttachmentPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        blob={previewBlob}
        fileName={fileName}
        title="Attachment"
      />
    </div>
  );
};

export default VehicleRequestAttachment;
